import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { Contract } from '../models/Contract';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';

export function useContracts(clientId?: string | null, organizationId?: string | null) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    setLoading(true);
    
    let q;
    let needsSorting = false;
    if (clientId) {
      // Filter by clientId - remove orderBy to avoid index requirement
      q = query(
        collection(db, 'contracts'),
        where('clientId', '==', clientId)
      );
      needsSorting = true;
    } else if (organizationId) {
      // Filter by organizationId - remove orderBy to avoid index requirement
      q = query(
        collection(db, 'contracts'),
        where('organizationId', '==', organizationId)
      );
      needsSorting = true;
    } else {
      // No filter - query all contracts (SUPER_ADMIN can see all)
      if (currentUser?.role === UserRole.SUPER_ADMIN) {
        q = query(
          collection(db, 'contracts'),
          orderBy('createdAt', 'desc')
        );
        // No sorting needed - Firestore handles it
      } else {
        setContracts([]);
        setLoading(false);
        return;
      }
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const contractsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            startDate: data.startDate?.toDate() || new Date(),
            endDate: data.endDate?.toDate(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Contract;
        });
        
        // Sort by createdAt descending (newest first) if we removed orderBy
        if (needsSorting) {
          contractsData.sort((a, b) => {
            const aTime = a.createdAt.getTime();
            const bTime = b.createdAt.getTime();
            return bTime - aTime; // Descending order
          });
        }
        
        setContracts(contractsData);
        setLoading(false);
        setError(null);
      },
      (err: any) => {
        console.error('Error fetching contracts:', err);
        // Check if it's an index building error
        const errorMessage = err?.message || '';
        if (err?.code === 'failed-precondition' && (errorMessage.includes('index') || errorMessage.includes('Index'))) {
          setError(new Error('Database indexes are currently building. Please wait a few minutes and refresh the page.'));
        } else {
          setError(err as Error);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [clientId, organizationId, currentUser?.role]);

  const createContract = async (contractData: Omit<Contract, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = Timestamp.now();
      await addDoc(collection(db, 'contracts'), {
        ...contractData,
        createdAt: now,
        updatedAt: now,
      });
    } catch (err) {
      console.error('Error creating contract:', err);
      throw err;
    }
  };

  const updateContract = async (contractId: string, updates: Partial<Contract>) => {
    try {
      const contractRef = doc(db, 'contracts', contractId);
      await updateDoc(contractRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error updating contract:', err);
      throw err;
    }
  };

  const deleteContract = async (contractId: string) => {
    try {
      const contractRef = doc(db, 'contracts', contractId);
      await deleteDoc(contractRef);
    } catch (err) {
      console.error('Error deleting contract:', err);
      throw err;
    }
  };

  return {
    contracts,
    loading,
    error,
    createContract,
    updateContract,
    deleteContract,
  };
}
